import React, { useState, useEffect, useRef, useCallback } from "react";
import authFetch from "../../authFetch";
import { smartHomeCan } from "../../permissions/checks";
import { fireNotification } from "../../notifications";
import { fireLocalNotification } from "../../native";
import { groupByArea, isEntityOn, SECURITY_DOMAINS, SECURITY_SENSOR_CLASSES } from "./haConstants";
import { EntityCard, areaSummary } from "./haEntities";
import useHomeAssistant from "./useHomeAssistant";
import SmartHomeSetup from "./SmartHomeSetup";
import SmartHomeRoomView from "./SmartHomeRoomView";
import SmartHomeScenes from "./SmartHomeScenes";
import SmartHomeSecurity from "./SmartHomeSecurity";
import SmartHomeNotifSettings from "./SmartHomeNotifSettings";
import SmartHomeCameras from "./SmartHomeCameras";
import SmartHomeEnergy from "./SmartHomeEnergy";
import SmartHomePersonalToken from "./SmartHomePersonalToken";
import SmartHomeAutomations from "./SmartHomeAutomations";

/* ══ SMART HOME MODULE ════════════════════════════════════ */
function SmartHomeModule({state,dispatch,t,currentUser,platformCtx}){
  const {locations=[],locFilter,lang}=state;
  const isRTL=lang==="ar";
  const connections=state.smartHomeConnections||{};

  const canView=smartHomeCan("viewSmartHome",currentUser,state,platformCtx);
  const canManage=smartHomeCan("manageConnections",currentUser,state,platformCtx);

  if(!canView) return (
    <div className="sh-empty">
      <p style={{textAlign:"center",color:"var(--g400)",padding:"3rem 1rem"}}>
        {isRTL?"ليس لديك صلاحية لعرض المنزل الذكي":"You do not have permission to view Smart Home."}
      </p>
    </div>
  );

  /* ── Location routing ── */
  const activeLocId=locFilter;

  /* No location filter — show location picker */
  if(!activeLocId){
    const connectedLocs=locations.filter(l=>connections[l.id]?.status==="connected");
    const unconnectedLocs=locations.filter(l=>!connections[l.id]||connections[l.id].status!=="connected");

    return (
      <div className="sh-module">
        <div className="sh-header">
          <h2 className="sh-title">{isRTL?"المنزل الذكي":"Smart Home"}</h2>
          <p className="sh-subtitle">{isRTL?"اختر موقعاً للبدء":"Select a location to get started"}</p>
        </div>

        {/* Connected locations */}
        {connectedLocs.length>0 && (
          <div className="sh-section">
            <h3 className="sh-section-title">{isRTL?"المواقع المتصلة":"Connected Locations"}</h3>
            <div className="sh-loc-grid">
              {connectedLocs.map(loc=>(
                <button key={loc.id} className="sh-loc-card sh-loc-connected" onClick={()=>dispatch({type:"SET_LOC",id:loc.id})}>
                  <div className="sh-loc-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M2 8l8-6 8 6"/><path d="M4 7v9h12V7"/><rect x="8" y="11" width="4" height="5"/></svg>
                  </div>
                  <span className="sh-loc-name">{isRTL&&loc.nameAr?loc.nameAr:loc.name}</span>
                  <span className="sh-loc-status sh-status-ok">{isRTL?"متصل":"Connected"}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Unconnected locations */}
        {unconnectedLocs.length>0 && canManage && (
          <div className="sh-section">
            <h3 className="sh-section-title">{isRTL?"المواقع غير المتصلة":"Available Locations"}</h3>
            <div className="sh-loc-grid">
              {unconnectedLocs.map(loc=>(
                <button key={loc.id} className="sh-loc-card sh-loc-available" onClick={()=>dispatch({type:"SET_LOC",id:loc.id})}>
                  <div className="sh-loc-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--g400)" strokeWidth="1.5"><path d="M2 8l8-6 8 6"/><path d="M4 7v9h12V7"/><path d="M8 12h4M10 10v4"/></svg>
                  </div>
                  <span className="sh-loc-name">{isRTL&&loc.nameAr?loc.nameAr:loc.name}</span>
                  <span className="sh-loc-status sh-status-setup">{isRTL?"إعداد":"Set up"}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {locations.length===0 && (
          <div className="sh-empty-state">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="var(--g300)" strokeWidth="1.5"><path d="M6 22l18-16 18 16"/><path d="M10 20v20h28V20"/><rect x="18" y="28" width="12" height="12"/></svg>
            <p>{isRTL?"أضف موقعاً في الإعدادات لبدء إعداد المنزل الذكي":"Add a location in Settings to start setting up Smart Home."}</p>
          </div>
        )}
      </div>
    );
  }

  /* ── Active location selected ── */
  const conn=connections[activeLocId];
  const loc=locations.find(l=>l.id===activeLocId);
  const locLabel=loc?(isRTL&&loc.nameAr?loc.nameAr:loc.name):"";

  /* Not connected — show setup wizard */
  if(!conn||conn.status!=="connected"){
    return (
      <div className="sh-module">
        <SmartHomeSetup
          locId={activeLocId}
          locLabel={locLabel}
          state={state}
          dispatch={dispatch}
          t={t}
          isRTL={isRTL}
          canManage={canManage}
        />
      </div>
    );
  }

  /* Connected — live dashboard */
  return <SmartHomeDashboard
    locId={activeLocId}
    locLabel={locLabel}
    conn={conn}
    state={state}
    dispatch={dispatch}
    isRTL={isRTL}
    currentUser={currentUser}
    platformCtx={platformCtx}
  />;
}

/* ── Connected dashboard (fetches token, wires hook) ───── */
function SmartHomeDashboard({locId,locLabel,conn,state,dispatch,isRTL,currentUser,platformCtx}){
  const tokenRef=useRef(null);
  const [tokenReady,setTokenReady]=useState(false);
  const [tokenError,setTokenError]=useState(null);
  const [tokenScope,setTokenScope]=useState(null); /* "personal" | "workspace" */
  const canControl=smartHomeCan("controlDevices",currentUser,state,platformCtx);
  const entityLabels=state.smartHomeEntityLabels||{};
  const [activeRoom,setActiveRoom]=useState(null);
  const [tab,setTab]=useState("dashboard"); /* dashboard | scenes | security | alerts */
  const notifPrefs=state.smartHomeNotifPrefs||{};

  /* ── Alert callback — fires in-app + local notifications on security events ── */
  const handleAlert=useCallback((alert)=>{
    /* Check if user has disabled this event type */
    if(notifPrefs[alert.eventKey]===false) return;

    const eventData={
      entityName:alert.entityName,
      area:alert.area,
      newState:alert.newState,
      oldState:alert.oldState,
    };

    /* In-app notification via existing system */
    fireNotification({eventKey:alert.eventKey,state,dispatch,platformCtx,eventData});

    /* Capacitor local notification (immediate, device-level) */
    const isAr=state.lang==="ar";
    const titles={
      sh_door_opened:isAr?"تم فتح باب / نافذة":"Door / Window Opened",
      sh_motion_detected:isAr?"تم كشف حركة":"Motion Detected",
      sh_lock_changed:isAr?"تغيير حالة القفل":"Lock State Changed",
      sh_alarm_triggered:isAr?"⚠ تم تفعيل الإنذار!":"⚠ Alarm Triggered!",
      sh_alarm_armed:isAr?"تغيير حالة الإنذار":"Alarm State Changed",
      sh_device_offline:isAr?"جهاز غير متصل":"Device Offline",
    };
    const body=`${alert.entityName}${alert.area?" — "+alert.area:""}`;
    fireLocalNotification(titles[alert.eventKey]||alert.eventKey,body,{eventKey:alert.eventKey,entityId:alert.entityId});
  },[notifPrefs,state,dispatch,platformCtx]);

  /* Fetch decrypted token on mount (held in ref, never in state) */
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const res=await authFetch("/api/ha-credentials?loc_id="+encodeURIComponent(locId));
        if(!res.ok){
          const body=await res.json().catch(()=>({}));
          throw new Error(body.error||"Token unavailable");
        }
        const body=await res.json();
        if(!cancelled){
          tokenRef.current=body.token;
          setTokenScope(body.scope||null);
          setTokenReady(true);
        }
      }catch(err){
        if(!cancelled) setTokenError(err.message);
      }
    })();
    return ()=>{cancelled=true;tokenRef.current=null;setTokenReady(false);};
  },[locId]);

  const haUrl=conn.haUrl;
  /* Pass locId so REST calls route through /api/ha-proxy (no CORS hoops).
     haUrl + token are still passed for the direct WebSocket connection. */
  const ha=useHomeAssistant(
    tokenReady?locId:null,
    tokenReady?haUrl:null,
    tokenReady?tokenRef.current:null,
    handleAlert
  );

  /* ── Loading / error states ── */
  if(tokenError) return (
    <div className="sh-module">
      <div className="sh-header">
        <h2 className="sh-title">{isRTL?"المنزل الذكي":"Smart Home"}</h2>
        <p className="sh-subtitle">{locLabel}</p>
      </div>
      <div className="sh-error" style={{margin:"2rem auto",maxWidth:400}}>{tokenError}</div>
    </div>
  );

  if(!tokenReady) return (
    <div className="sh-module">
      <div className="sh-header">
        <h2 className="sh-title">{isRTL?"المنزل الذكي":"Smart Home"}</h2>
        <p className="sh-subtitle">{locLabel}</p>
      </div>
      <p style={{textAlign:"center",color:"var(--g400)",padding:"2rem"}}>{isRTL?"جارٍ التحميل...":"Loading..."}</p>
    </div>
  );

  /* ── Destructure hook ── */
  const {connected,connectionError,entities,areas,scenes,callService,triggerScene}=ha;

  /* ── Derived data ── */
  const lights=entities.filter(e=>e.domain==="light");
  const lightsOn=lights.filter(isEntityOn).length;
  const locks=entities.filter(e=>e.domain==="lock");
  const locksUnlocked=locks.filter(e=>e.state==="unlocked").length;
  const climates=entities.filter(e=>e.domain==="climate");
  const tempSensors=entities.filter(e=>e.domain==="sensor"&&(e.attributes.device_class==="temperature"||e.attributes.unit_of_measurement==="°C"||e.attributes.unit_of_measurement==="°F"));
  const areaGroups=groupByArea(entities.filter(e=>e.domain!=="scene"),areas);
  const areaList=Object.values(areaGroups).sort((a,b)=>a.name==="Other"?1:b.name==="Other"?-1:a.name.localeCompare(b.name));

  /* Security tab visibility — only show when security entities exist */
  const hasSecurityEntities=entities.some(e=>
    SECURITY_DOMAINS.has(e.domain)||(e.domain==="binary_sensor"&&SECURITY_SENSOR_CLASSES.has(e.attributes?.device_class))
  );
  const cameraEntities=entities.filter(e=>e.domain==="camera");
  const hasEnergyEntities=entities.some(e=>e.domain==="sensor"&&(e.attributes?.device_class==="energy"||e.attributes?.device_class==="power"));
  const automationEntities=entities.filter(e=>e.domain==="automation");

  /* ── Room detail view (sub-route within dashboard tab) ── */
  if(activeRoom&&tab==="dashboard"){
    const roomData=areaGroups[activeRoom];
    if(roomData) return (
      <div className="sh-module">
        <SmartHomeRoomView
          area={roomData}
          isRTL={isRTL}
          entityLabels={entityLabels}
          canControl={canControl}
          callService={callService}
          onBack={()=>setActiveRoom(null)}
          onSaveLabel={(entityId,label)=>{
            const labels={...entityLabels,[entityId]:label};
            dispatch({type:"SET_CONFIG_KEY",payload:{key:"smartHomeEntityLabels",value:labels}});
          }}
        />
      </div>
    );
    setActiveRoom(null);
  }

  return (
    <div className="sh-module">
      <div className="sh-header">
        <h2 className="sh-title">{isRTL?"المنزل الذكي":"Smart Home"}</h2>
        <p className="sh-subtitle">{locLabel}</p>
      </div>

      {/* Connection banner */}
      <div className={`sh-status-banner ${connected?"sh-status-ok":connectionError?"sh-status-err":"sh-status-warn"}`}>
        {connected ? (
          <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="5"/><path d="M4.5 7l2 1.5 3-3"/></svg>
          <span>{isRTL?"متصل":"Connected"}</span></>
        ) : connectionError ? (
          <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="5"/><path d="M5 5l4 4M9 5l-4 4"/></svg>
          <span>{isRTL?"غير متصل":"Unreachable"} — {connectionError}</span></>
        ) : (
          <><svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="5"/><path d="M4 7h6"/></svg>
          <span>{isRTL?"جارٍ الاتصال...":"Connecting..."}</span></>
        )}
      </div>

      {/* Tab navigation */}
      <div className="sh-tabs">
        <button className={`sh-tab${tab==="dashboard"?" sh-tab-active":""}`} onClick={()=>{setTab("dashboard");setActiveRoom(null);}}>
          {isRTL?"لوحة التحكم":"Dashboard"}
        </button>
        {scenes.length>0&&(
          <button className={`sh-tab${tab==="scenes"?" sh-tab-active":""}`} onClick={()=>setTab("scenes")}>
            {isRTL?"المشاهد":"Scenes"}
            <span className="sh-tab-badge">{scenes.length}</span>
          </button>
        )}
        {hasSecurityEntities&&(
          <button className={`sh-tab${tab==="security"?" sh-tab-active":""}`} onClick={()=>setTab("security")}>
            {isRTL?"الأمان":"Security"}
            {locksUnlocked>0&&<span className="sh-tab-badge sh-badge-warn">{locksUnlocked}</span>}
          </button>
        )}
        {cameraEntities.length>0&&(
          <button className={`sh-tab${tab==="cameras"?" sh-tab-active":""}`} onClick={()=>setTab("cameras")}>
            {isRTL?"الكاميرات":"Cameras"}
            <span className="sh-tab-badge">{cameraEntities.length}</span>
          </button>
        )}
        {hasEnergyEntities&&(
          <button className={`sh-tab${tab==="energy"?" sh-tab-active":""}`} onClick={()=>setTab("energy")}>
            {isRTL?"الطاقة":"Energy"}
          </button>
        )}
        {automationEntities.length>0&&(
          <button className={`sh-tab${tab==="automations"?" sh-tab-active":""}`} onClick={()=>setTab("automations")}>
            {isRTL?"الأتمتة":"Automations"}
            <span className="sh-tab-badge">{automationEntities.length}</span>
          </button>
        )}
        <button className={`sh-tab${tab==="alerts"?" sh-tab-active":""}`} onClick={()=>setTab("alerts")}>
          {isRTL?"الإشعارات":"Alerts"}
        </button>
        <button className={`sh-tab${tab==="settings"?" sh-tab-active":""}`} onClick={()=>setTab("settings")}>
          {isRTL?"الإعدادات":"Settings"}
          {tokenScope==="personal"&&<span className="sh-tab-badge" style={{background:"var(--gold-bg)",color:"var(--gold)"}}>{isRTL?"شخصي":"Personal"}</span>}
        </button>
      </div>

      {/* ══ TAB: Dashboard ══ */}
      {tab==="dashboard"&&<>
        {/* Quick stats */}
        <div className="sh-stats">
          {lights.length>0 && (
            <div className="sh-stat">
              <span className="sh-stat-val">{lightsOn}/{lights.length}</span>
              <span className="sh-stat-lbl">{isRTL?"أضواء مشتعلة":"Lights on"}</span>
            </div>
          )}
          {locks.length>0 && (
            <div className="sh-stat">
              <span className={`sh-stat-val${locksUnlocked>0?" sh-val-warn":""}`}>{locksUnlocked>0?locksUnlocked:locks.length}</span>
              <span className="sh-stat-lbl">{locksUnlocked>0?(isRTL?"أقفال مفتوحة":"Unlocked"):(isRTL?"أقفال مغلقة":"Locked")}</span>
            </div>
          )}
          {tempSensors.length>0 && (
            <div className="sh-stat">
              <span className="sh-stat-val">{tempSensors[0].state}°</span>
              <span className="sh-stat-lbl">{isRTL?"درجة الحرارة":"Temperature"}</span>
            </div>
          )}
          {climates.length>0 && (
            <div className="sh-stat">
              <span className="sh-stat-val">{climates.filter(isEntityOn).length}/{climates.length}</span>
              <span className="sh-stat-lbl">{isRTL?"مكيفات":"Climate"}</span>
            </div>
          )}
        </div>

        {/* Area / room cards */}
        {areaList.length>0 ? (
          <div className="sh-areas">
            {areaList.map(area=>{
              const summary=areaSummary(area.entities);
              return (
                <div key={area.areaId} className="sh-area-card card" style={{cursor:"pointer"}} onClick={()=>setActiveRoom(area.areaId)}>
                  <div className="ch">
                    <span style={{fontWeight:600,fontSize:14}}>{area.name}</span>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {summary.lightsTotal>0&&<span style={{fontSize:11,color:summary.lightsOn>0?"var(--gold)":"var(--g400)"}}>{summary.lightsOn}/{summary.lightsTotal} {isRTL?"إضاءة":"lights"}</span>}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--g400)" strokeWidth="1.5" strokeLinecap="round"><path d={isRTL?"M7 2L3 6l4 4":"M5 2l4 4-4 4"}/></svg>
                    </div>
                  </div>
                  <div className="sh-ecard-grid" style={{padding:"8px 12px"}}>
                    {area.entities.slice(0,6).map(ent=>(
                      <EntityCard key={ent.entity_id} entity={ent} isRTL={isRTL} customLabel={entityLabels[ent.entity_id]}/>
                    ))}
                    {area.entities.length>6&&(
                      <div className="sh-ecard sh-ecard-more"><span>+{area.entities.length-6}</span></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          entities.length===0 && connected && (
            <p style={{textAlign:"center",color:"var(--g400)",padding:"2rem"}}>{isRTL?"لا توجد أجهزة":"No devices found."}</p>
          )
        )}
      </>}

      {/* ══ TAB: Scenes ══ */}
      {tab==="scenes"&&(
        <SmartHomeScenes scenes={scenes} triggerScene={triggerScene} canControl={canControl} isRTL={isRTL}/>
      )}

      {/* ══ TAB: Security ══ */}
      {tab==="security"&&(
        <SmartHomeSecurity entities={entities} callService={callService} canControl={canControl} isRTL={isRTL}/>
      )}

      {/* ══ TAB: Cameras ══ */}
      {tab==="cameras"&&(
        <SmartHomeCameras cameras={cameraEntities} locId={locId} haUrl={haUrl} token={tokenRef.current} isRTL={isRTL}/>
      )}

      {/* ══ TAB: Energy ══ */}
      {tab==="energy"&&(
        <SmartHomeEnergy entities={entities} locId={locId} isRTL={isRTL}/>
      )}

      {/* ══ TAB: Automations ══ */}
      {tab==="automations"&&(
        <SmartHomeAutomations
          entities={entities}
          scenes={scenes}
          locId={locId}
          callService={callService}
          canControl={canControl}
          isRTL={isRTL}
        />
      )}

      {/* ══ TAB: Alerts ══ */}
      {tab==="alerts"&&(
        <SmartHomeNotifSettings
          prefs={notifPrefs}
          isRTL={isRTL}
          onToggle={(key,val)=>{
            const updated={...notifPrefs,[key]:val};
            dispatch({type:"SET_CONFIG_KEY",payload:{key:"smartHomeNotifPrefs",value:updated}});
          }}
        />
      )}

      {/* ══ TAB: Settings (personal + shared HA tokens) ══ */}
      {tab==="settings"&&(
        <SmartHomePersonalToken
          locId={locId}
          locLabel={locLabel}
          currentUser={currentUser}
          isRTL={isRTL}
          isOwner={currentUser?.role==="owner"}
        />
      )}
    </div>
  );
}

export default SmartHomeModule;
